import React, { forwardRef } from 'react';
import type { InputHTMLAttributes } from 'react';
import classNames from '../../utils/classNames';

/** The border and corners shared by form fields and field-like controls. */
export const fieldFrameClasses = 'rounded border border-neutral-300';

export const baseFieldClasses = `focus:shadow-outline w-full appearance-none ${fieldFrameClasses} bg-white px-2 read-only:cursor-not-allowed disabled:cursor-not-allowed disabled:pointer-events-none leading-normal focus:outline-none`;

export const inputFieldClasses = `${baseFieldClasses} text-form-text`;

export enum INPUT_SIZE {
    SMALL = 'SMALL',
    MEDIUM = 'MEDIUM',
    LARGE = 'LARGE',
}

const SIZE_CLASSES = Object.freeze({
    [INPUT_SIZE.SMALL]: 'py-1',
    [INPUT_SIZE.MEDIUM]: 'py-2',
    [INPUT_SIZE.LARGE]: 'py-4',
});

const Input = forwardRef<HTMLInputElement, InputProps>(
    ({ className, size = INPUT_SIZE.LARGE, type = 'text', error, style, ...rest }, ref) => {
        // `disabled:pointer-events-none` is to allow onClick event on disabled
        // input's in Chrome. This is needed to show the `areYouSureToOverride` confirm.
        const classes =
            type === 'checkbox'
                ? ''
                : classNames(
                      inputFieldClasses,
                      SIZE_CLASSES[size],
                      error && 'border-red-500',
                      rest.readOnly && 'cursor-not-allowed',
                      className
                  );

        return (
            <input
                ref={ref}
                className={classes}
                style={error ? { borderColor: '#ef4444', ...style } : style}
                type={type}
                {...rest}
            />
        );
    }
);

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
    error?: boolean | string | Record<string, unknown>;
    size?: INPUT_SIZE;
}

Input.displayName = 'Input';

export default Input;
